import { CHARGE_SCENARIO_LABELS } from '../constants/chargeFilterSchema';
import { invoiceAmountValue } from './invoiceAmounts';

export interface DistinctValues {
    count: number;
    single: string | null;
}

export interface InvoiceSummary {
    count: number;
    operations: DistinctValues;
    issuers: DistinctValues;
    payers: DistinctValues;
    natures: Record<string, number>;
    statuses: Record<string, number>;
    collect: Record<string, number>;
    titleAmount: number;
    invoiceAmount: number;
}

export interface SummaryGroup {
    label: string;
    count: number;
    titleAmount: number;
}

export interface InvoiceBreakdown {
    natures: SummaryGroup[];
    statuses: SummaryGroup[];
    collect: SummaryGroup[];
    operations: SummaryGroup[];
    issuers: SummaryGroup[];
    payers: SummaryGroup[];
}

const NO_NATURE = 'Sem natureza';
const NOT_INFORMED = 'Não informado';

export const collectText = (row: any): string => {
    const flags = [row?.subrogation && 'Sub-rogado', row?.judicialPetition && 'Jurídico']
        .filter(Boolean);
    if (flags.length) return flags.join(' + ');
    return row?.collect ? 'Sim' : 'Não';
};

export const distinct = (rows: any[], keyOf: (row: any) => string | null, labelOf: (row: any) => string | null): DistinctValues => {
    const labels = new Map<string, string>();
    rows.forEach(row => {
        const key = keyOf(row);
        if (key) labels.set(key, labelOf(row) || key);
    });
    return { count: labels.size, single: labels.size === 1 ? Array.from(labels.values())[0] : null };
};

export const countBy = (rows: any[], labelOf: (row: any) => string): Record<string, number> =>
    rows.reduce((acc: Record<string, number>, row) => {
        const label = labelOf(row);
        acc[label] = (acc[label] || 0) + 1;
        return acc;
    }, {});

export const sumCents = (values: (number | null)[]): number =>
    values.reduce((cents: number, v) => (v == null || Number.isNaN(v) ? cents : cents + Math.round(v * 100)), 0) / 100;

export const partyKey = (party: any): string | null => party?.documentNumber || party?.name || null;

const statusLabel = (row: any): string | null => row?.statusFormatted || row?.status || null;

const natureLabel = (row: any): string =>
    (row?.businessReference ? CHARGE_SCENARIO_LABELS[row.businessReference] || row.businessReference : NO_NATURE);

const titleAmountOf = (row: any): number | null => (row?.titleAmount == null ? null : Number(row.titleAmount));

export const summarizeInvoices = (rows: any[]): InvoiceSummary => ({
    count: rows.length,
    operations: distinct(rows, r => r?.creditOperationId || r?.operationNumber || null,
        r => r?.operationNumber || r?.contractLabel || null),
    issuers: distinct(rows, r => partyKey(r?.issuer), r => r?.issuer?.name || null),
    payers: distinct(rows, r => partyKey(r?.payer), r => r?.payer?.name || null),
    natures: countBy(rows, natureLabel),
    statuses: countBy(rows, r => statusLabel(r) || '—'),
    collect: countBy(rows, collectText),
    titleAmount: sumCents(rows.map(titleAmountOf)),
    invoiceAmount: sumCents(rows.map(invoiceAmountValue)),
});

const groupBy = (rows: any[], keyOf: (row: any) => string | null, labelOf: (row: any) => string | null): SummaryGroup[] => {
    const groups = new Map<string, { label: string; rows: any[] }>();
    rows.forEach(row => {
        const key = keyOf(row) ?? NOT_INFORMED;
        const group = groups.get(key) ?? { label: labelOf(row) || key, rows: [] };
        group.rows.push(row);
        groups.set(key, group);
    });
    return Array.from(groups.values())
        .map(g => ({ label: g.label, count: g.rows.length, titleAmount: sumCents(g.rows.map(titleAmountOf)) }))
        .sort((a, b) => b.titleAmount - a.titleAmount || b.count - a.count);
};

export const breakdownInvoices = (rows: any[]): InvoiceBreakdown => ({
    natures: groupBy(rows, natureLabel, natureLabel),
    statuses: groupBy(rows, statusLabel, statusLabel),
    collect: groupBy(rows, collectText, collectText),
    operations: groupBy(rows, r => r?.creditOperationId || r?.operationNumber || null,
        r => r?.operationNumber || r?.contractLabel || null),
    issuers: groupBy(rows, r => partyKey(r?.issuer), r => r?.issuer?.name || null),
    payers: groupBy(rows, r => partyKey(r?.payer), r => r?.payer?.name || null),
});
